import { useEffect, useMemo } from 'react';
import { DeepPartial, FieldPath, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InferType, object, ObjectSchema, string } from 'yup';

import {
  Button,
  FormField,
  FormSubmitButton,
  GridCol,
  GridRow,
  Input,
  Modal,
  ModalContent,
  Label,
  ModalFooter,
  Select,
} from '@client/toolkit';
import { yupResolver } from '@hookform/resolvers/yup';
import { handleBackendValidationErrors } from '@client/api';

type FormModel = {
  email: string;
  role: string;
};

const schema: ObjectSchema<FormModel> = object({
  email: string().email().required(),
  role: string().oneOf(['ClientAdmin', 'ClientUser']).required(),
});

type FormValues = InferType<typeof schema>;
type FormPath = FieldPath<FormModel>;

export type UserInviteFormData = FormValues;

interface UserInviteFormProps {
  title: React.ReactNode;
  onSubmit: (data: FormValues) => Promise<void>;
  onClose: () => void;
  person?: FormModel;
  open?: boolean;
}

export function UserInviteForm({ open = true, title, onSubmit, onClose, person }: UserInviteFormProps) {
  const { t } = useTranslation('type');
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');

  const defaultValues: FormValues = useMemo(
    () => ({
      email: person?.email ?? '',
      role: person?.role ?? 'ClientAdmin',
    }),
    [person?.email],
  );

  const methods = useForm<FormValues>({
    defaultValues,
    mode: 'onTouched',
    resolver: yupResolver(schema),
  });

  const { handleSubmit, reset, setError } = methods;

  const handleFormSubmit = async (data: FormValues) => {
    await onSubmit(data).catch((error) => {
      handleBackendValidationErrors(
        {
          Email: 'email',
        },
        error,
        (name, error) => setError(name as FormPath, error),
      );
    });
  };

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, open]);

  return (
    <Modal open={open} size="lg" onClose={onClose} title={title}>
      <FormProvider {...methods}>
        <form noValidate onSubmit={handleSubmit(handleFormSubmit)}>
          <ModalContent className="space-y-8 p-4">
            <article>
              <GridRow cols={{ def: 1, sm: 6 }} spacingX={2} spacingY={4}>
                <GridCol span={{ def: 1, sm: 6 }}>
                  <FormField<FormValues, FormPath>
                    name="email"
                    label={tGlobal('user.email')}
                    required
                    render={({ field }) => <Input {...field} />}
                  />
                </GridCol>

                <GridCol span={{ def: 1, sm: 6 }}>
                  <FormField<FormValues, FormPath>
                    name="role"
                    label={tGlobal('user.role')}
                    required
                    render={({ field }) => (
                      <Select {...field}>
                        <option value="ClientAdmin">{t('UserRole.ClientAdmin')}</option>
                        <option value="ClientUser">{t('UserRole.ClientUser')}</option>
                      </Select>
                    )}
                  />
                </GridCol>
              </GridRow>
            </article>
          </ModalContent>

          <ModalFooter>
            <Button color="primary-dark" onClick={onClose}>
              {tCommon('back')}
            </Button>
            <FormSubmitButton type="submit">{tCommon('send')}</FormSubmitButton>
          </ModalFooter>
        </form>
      </FormProvider>
    </Modal>
  );
}
